import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import AssemblyModel from '../components/Overview/SensorModel';
import SensorsSection from '../components/Overview/SensorsSection';
import CalibrationSection from '../components/Overview/CalibrationSection';
import DatasetTree from '../components/Overview/DatasetTree';
import { fetchSensorCalibrationZip } from '../services/api';
import { API_BASE_URL } from '../constants/config';
import { LINKS } from '../constants/links';

type TrajectoryName =
  | 'Red'
  | 'Blue'
  | 'Orange'
  | 'Green'
  | 'Magenta'
  | 'Yellow';

export default function Overview() {
  const [selected, setSelected] = useState<TrajectoryName>('Red');
  const [selectedSensorKey, setSelectedSensorKey] = useState<string | null>(
    null
  );
  const [pageLoaded, setPageLoaded] = useState(false);
  const [timeSyncError, setTimeSyncError] = useState<string | null>(null);
  const [timeSyncWarning, setTimeSyncWarning] = useState<string | null>(null);

  useEffect(() => {
    const mapContainer = document.getElementById('map');
    if (!mapContainer || mapContainer.innerHTML !== '') return;

    const gpxScript = document.createElement('script');
    gpxScript.src =
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet-gpx/1.7.0/gpx.min.js';
    gpxScript.async = true;
    gpxScript.onload = async () => {
      if ((mapContainer as any)._leaflet_id) return;

      const map = L.map('map').setView([47.322617, -71.145015], 14);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      const loadGPXFile = (trajectory: any) => {
        const polylineOptions = {
          color: trajectory.color,
          opacity: 0.75,
          weight: 5,
          lineCap: 'round',
        };
        const gpxLayer = new (L as any).GPX(trajectory.url, {
          async: true,
          polyline_options: polylineOptions,
          marker_options: {
            startIconUrl: '',
            endIconUrl: '',
            shadowUrl: '',
          },
        });
        gpxLayer.on('loaded', function (e: any) {
          const polyline = e.target.getLayers()[0];
          polyline.bindPopup(
            `<b>${trajectory.name}</b><br>Length: ${e.target.get_distance().toFixed(0)} m<br>${trajectory.info}`
          );
        });
        gpxLayer.addTo(map);
      };

      const res = await fetch(`${API_BASE_URL}/api/trajectories`);
      const trajectories = await res.json();
      trajectories.forEach(loadGPXFile);
    };

    document.body.appendChild(gpxScript);
  }, []);

  useEffect(() => {
    const handleWebGLContextLost = (e: Event) => {
      e.preventDefault();
      alert('WebGL context lost. Please reload the page.');
    };
    window.addEventListener('webglcontextlost', handleWebGLContextLost, false);
    return () =>
      window.removeEventListener('webglcontextlost', handleWebGLContextLost);
  }, []);

  useEffect(() => {
    const handleLoad = () => setPageLoaded(true);

    if (document.readyState === 'complete') {
      setPageLoaded(true);
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  const trajectories = {
    Red: (
      <>
        This is the shortest trajectory in the dataset, covering 302&nbsp;meters{' '}
        <strong>on-road</strong>. The terrain is semi-urban, making a loop on
        the road next to the main building of the forest. The start, as well as
        most of the roads used in the trajectory are used in the blue and orange
        trajectories.
      </>
    ),
    Blue: (
      <>
        Starting in a semi-urban area, this trajectory leads the robot downhill
        into the forest before looping back around the main building and coming
        back to its starting point. It combines <strong>on-road</strong>,{' '}
        <strong>off-road</strong>
        and <strong>off-road</strong> sections over 544&nbsp;meters. During
        winter, the forest roads have high snow cover, especially being the
        building in the uphill section.
      </>
    ),
    Orange: (
      <>
        Like the blue trajectory, the orange trajectory starts next to the main
        building and goes towards a downhill in the forest, combining{' '}
        <strong>on-road</strong> and <strong>off-road</strong> navigation. The
        robot then continues on the forest roads, and goes through the stone
        quarry an <strong>off-trail</strong> section, which is explored in more
        details in the magenta trajectory. Once across the stone quarry, the
        robot takes the gravel road to come back to its starting point. This
        trajectory is the longest of the dataset with
        2173&nbsp;total&nbsp;meters. During dry summer days, dust clouds can be
        observed on the gravel road.
      </>
    ),
    Green: (
      <>
        The green trajectory is characterized by the navigation difficulties it
        imposes with its steep ascent and descent, as well as the dense
        vegetation that limits the GNSS coverage and blocks the camera field of
        view. The trajectory is short with 357&nbsp;meters in{' '}
        <strong>off-trail</strong> condition and a lot of elevation. During
        winter, the deep snow, paired with the steep terrain in this section of
        the forest caused some traversability issues. This led to recording some
        flatter trajectories, also in a dense vegetation and deep snow, but
        avoiding the steep hills.
      </>
    ),
    Magenta: (
      <>
        This trajectory begins at the top of the stone quarry and loops through
        the entire area before returning to the starting point. In winter, deep
        snow and hidden obstacles pose navigational challenges, while in summer,
        exposed stones and trees make navigation more difficult. The trajectory
        also crosses a creek, causing the robot to experience a high pitch
        motion. Due to its wide open area and irregular terrain, the stone
        quarry can also present significant challenges for LiDAR data
        collection. This trajectory can vary in length depending on the path
        chosen to navigate, but is around 700&nbsp;meters in{' '}
        <strong>off-trail</strong> condition.
      </>
    ),
    Yellow: (
      <>
        This trajectory combines roads and forest navigation on a long
        trajectory of 1920&nbsp;meters <strong>on-road</strong> and{' '}
        <strong>off-road</strong>. In winter, the forest section includes a big
        snowbank as well as a long uphill section that need to be climbed. The
        yellow trajectory is the only one of the dataset that is not a closed
        loop as the bridge on the road was inaccessible during the data
        recording period.
      </>
    ),
  };

  const trajectoryConfig: Record<
    TrajectoryName,
    { color: string; image: string }
  > = {
    Red: { color: 'bg-red-600', image: 'assets/traj_images/red_overview.webp' },
    Blue: {
      color: 'bg-blue-600',
      image: 'assets/traj_images/blue_overview.webp',
    },
    Orange: {
      color: 'bg-orange-500',
      image: 'assets/traj_images/orange_overview.webp',
    },
    Green: {
      color: 'bg-green-600',
      image: 'assets/traj_images/green_overview.webp',
    },
    Magenta: {
      color: 'bg-pink-600',
      image: 'assets/traj_images/magenta_overview.webp',
    },
    Yellow: {
      color: 'bg-yellow-500',
      image: 'assets/traj_images/yellow_overview.webp',
    },
  };

  return (
    <div className="w-full px-6 py-12 text-gray-800 dark:text-gray-200 ">
      <section className="mb-20">
        <h2
          id="Introduction"
          className="scroll-mt-24 text-4xl text-center font-extrabold tracking-tight mb-6"
        >
          Introduction
        </h2>
        <div className="bg-white dark:bg-neutral-800 p-6 rounded-lg shadow-md max-w-5xl mx-auto">
          <p className="text-lg text-justify text-gray-800 dark:text-gray-200">
            The FoMo dataset is a multi-season collection designed to support
            research on robust algorithms for robot autonomy under adverse
            conditions. We expect that the dataset and its associated benchmarks
            will present a meaningful challenge for state-of-the-art SLAM and
            odometry methods. It includes repeated traversals of six
            trajectories of varying complexity, enabling long-term evaluation of
            SLAM and odometry performance. In addition, the dataset provides
            rich metadata, such as detailed measurements from an on-site weather
            station recorded at one-minute intervals. For more details, see our
            paper:{' '}
            <a
              href="https://arxiv.org/pdf/2603.08433"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              FoMo: A Multi-Season Dataset for Robot Navigation in Forêt
              Montmorency
            </a>
            .
          </p>
          <div className="mt-6 text-center">
            <a
              href={LINKS.BLOG}
              className="text-blue-500 hover:text-blue-700 flex justify-center items-center space-x-2 transform transition-all duration-300 hover:scale-105 hover:shadow-lg rounded-lg py-2 px-4 border-2 border-transparent hover:border-blue-500"
            >
              <span className="text-lg font-semibold">
                About the FoMo Dataset Logo
              </span>
              <span className="text-xl animate-pulse">👆</span>
            </a>
          </div>
        </div>
      </section>

      <section>
        <h2
          id="trajectories"
          className="scroll-mt-24 text-2xl text-center font-bold mb-3"
        >
          Trajectories Descriptions
        </h2>

        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {(Object.keys(trajectories) as TrajectoryName[]).map((color) => (
            <button
              key={color}
              onClick={() => setSelected(color)}
              className={`px-4 py-2 rounded-xl font-semibold shadow transition
             ${
               selected === color
                 ? `${trajectoryConfig[color].color} text-white`
                 : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
             }`}
            >
              {color}
            </button>
          ))}
        </div>

        <div className="max-w-5xl mx-auto p-6 rounded-xl flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-2">{selected}</h3>
            <p className="text-lg text-justify">{trajectories[selected]}</p>
          </div>

          <div className="flex-shrink-0">
            <img
              src={trajectoryConfig[selected].image}
              alt={`${selected} trajectory`}
              className="w-72 h-72 object-contain"
            />
          </div>
        </div>
      </section>

      <section className="mb-20">
        <h2
          id="platform"
          className="scroll-mt-24 text-4xl text-center font-extrabold tracking-tight mb-6"
        >
          Platform
        </h2>

        <div className="flex flex-col lg:flex-row items-start gap-6 max-w-5xl mx-auto mb-6">
          <div className="flex-shrink-0">
            <img
              src="assets/images/warthog.webp"
              alt="Clearpath Warthog Robot"
              className="w-72 h-72 object-contain"
            />
          </div>

          <div className="flex-1">
            <p className="text-lg text-justify">
              Our mobile robot is a Clearpath Warthog that operates on wheels
              during most of the year, but switches to a tracked drivetrain in
              winter to improve mobility on snow. The UGV is equipped with a
              range of proprioceptive and exteroceptive sensors, all mounted on
              a custom-designed modular sensor frame. Use the 3D model below to
              explore the sensor frame from different viewpoints!
            </p>
          </div>
        </div>
        <div className="flex justify-center">
          <div className="w-full max-w-4xl">
            {pageLoaded ? (
              <AssemblyModel selectedSensorKey={selectedSensorKey} />
            ) : (
              <div className="text-center py-10 text-gray-500 animate-pulse">
                Loading 3D model…
              </div>
            )}
          </div>
        </div>
        <SensorsSection onSensorSelect={setSelectedSensorKey} />
      </section>

      <CalibrationSection />

      <section className="mb-20 px-4 sm:px-6 lg:px-8">
        <h2
          id="time-sync-calibration"
          className="scroll-mt-24 text-3xl sm:text-4xl text-center font-extrabold tracking-tight mb-6"
        >
          <a
            href="#time-sync-calibration"
            className="text-gray-800 dark:text-gray-200 hover:text-blue-500 no-underline"
          >
            Time Synchronization
          </a>
        </h2>

        <div className="flex justify-center">
          <img
            src="assets/images/communication_diagram.webp"
            alt="Communication Diagram"
            className="w-full max-w-5xl h-auto object-contain mt-4 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-base sm:text-lg text-justify max-w-5xl mx-auto mt-6">
          The IEEE1588 Precision Time Protocol (PTP) is used to eliminate the
          discrepancy between devices on our system. The high-level computer,
          directly connected to the GNSS receiver from which it receives Pulses
          Per Second (PPS) signal, serves as a PTP Grandmaster and maintains
          synchronized time between devices. With the exception of the Trendnet
          Switch, all ethernet devices support hardware PTP.
        </p>

        <div className="flex flex-col lg:flex-row items-center gap-6 max-w-5xl mx-auto mt-6">
          <p className="text-base sm:text-lg text-justify flex-1">
            While the Jetson Orin AGX is time synchronized through PTP, the
            images from the ZED X camera are timestamped in the camera's driver.
            To mitigate this issue, we employ a simple strategy to estimate the
            time delay between the camera shutter time and the timestamp
            assigned by the camera driver. We generate a series of matrix
            barcodes, each embedding absolute UNIX time information, and capture
            them using a continuous video stream from the ZED X camera. An
            offline pipeline then decodes each barcode and calculates the delay
            by comparing the embedded time with the corresponding driver
            timestamp. The delay is estimated as the difference between the
            driver timestamp and the sum of the embedded time and the QR code
            generation time. With a sufficiently long recorded sequence, the
            estimated mean camera delay amounts to 51.48 ms with a standard
            deviation of 8.78 ms.
          </p>

          <img
            src="assets/images/zedx-latency.webp"
            alt="ZED X camera latency estimation method"
            className="w-full sm:w-3/4 lg:w-1/2 h-auto object-contain rounded-lg shadow-sm"
          />
        </div>
        <div className="flex flex-col items-center mt-6">
          <button
            onClick={async () => {
              try {
                setTimeSyncError(null);
                setTimeSyncWarning(
                  'Downloading started. It may take some time before it appears in your downloads...'
                );

                await fetchSensorCalibrationZip('camera-latency.zip');

                setTimeout(() => setTimeSyncWarning(null), 5000);
              } catch (err: any) {
                setTimeSyncWarning(null);
                setTimeSyncError(
                  err.message || 'An error occurred while downloading.'
                );
              }
            }}
            className="
      px-5 py-2.5 rounded-xl font-semibold
      bg-blue-600 text-white
      hover:bg-blue-700 active:bg-blue-800
      dark:bg-blue-500 dark:hover:bg-blue-600
      transition-all shadow-md hover:shadow-lg
      flex items-center gap-2
    "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12l4.5 4.5m0 0l4.5-4.5m-4.5 4.5V3"
              />
            </svg>
            Download camera latency rosbag
          </button>

          {timeSyncError && (
            <p className="mt-3 text-red-500 font-medium text-center">
              ⚠️ {timeSyncError}
            </p>
          )}
          {timeSyncWarning && (
            <p className="mt-3 text-yellow-600 font-medium">
              ⚠️ {timeSyncWarning}
            </p>
          )}
        </div>
      </section>
      <section className="mb-20 px-4 sm:px-6 lg:px-8">
        <h2
          id="download-information"
          className="scroll-mt-24 text-4xl text-center font-extrabold tracking-tight mb-10"
        >
          Download Information
        </h2>

        <div
          className="
    bg-white dark:bg-neutral-800
    p-8 rounded-2xl shadow-xl
    max-w-5xl mx-auto
    border border-gray-200 dark:border-neutral-700
  "
        >
          <p className="text-lg text-justify text-gray-800 dark:text-gray-200 mb-8">
            We provide the dataset in two data formats: a
            <span className="font-semibold">
              {' '}
              human-readable representation{' '}
            </span>
            and the binary <span className="font-semibold">ROS2 MCAP</span>{' '}
            files. Both formats are downloaded as a bash script that you run
            locally to retrieve and process your data.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div
              className="
        bg-gray-50 dark:bg-neutral-900
        p-6 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700
        hover:shadow-md transition
      "
            >
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="text-blue-600 dark:text-blue-400 text-2xl">
                  📘
                </span>
                Human-Readable Format
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-justify">
                This format contains all raw sensor data, metadata, and
                calibration files, directly accessible for researchers who need
                clear, interpretable data.
              </p>
              <p className="mt-4 text-gray-700 dark:text-gray-300">
                💡{' '}
                <span className="font-medium">
                  A bash script is generated and downloaded. Run it locally with{' '}
                  <a
                    href="https://aws.amazon.com/cli/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-semibold transition-colors"
                  >
                    AWS CLI
                  </a>{' '}
                  to retrieve your data.
                </span>
              </p>
            </div>

            <div
              className="
        bg-gray-50 dark:bg-neutral-900
        p-6 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700
        hover:shadow-md transition
      "
            >
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="text-purple-600 dark:text-purple-400 text-2xl">
                  📦
                </span>
                ROS2 MCAP Format
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-justify">
                The MCAP format is ideal for robotics frameworks and ROS2-based
                pipelines. It provides compact, fast playback of sensor streams.
              </p>
              <p className="mt-4 text-gray-700 dark:text-gray-300">
                ⚠️ {' '}
                <span className="font-medium">
MCAP download is not yet available. Please use the Human readable format in the meantime.
                </span>
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4">How to Download</h3>

          <ol className="list-decimal list-inside space-y-4 text-lg text-gray-800 dark:text-gray-200">
            <li>
              Go to the{' '}
              <a
                href="https://fomo.norlab.ulaval.ca/download"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Download
              </a>{' '}
              page.
            </li>

            <li>
              Choose a format:{' '}
              <span className="font-semibold">Human-Readable</span> or{' '}
              <span className="font-semibold">MCAP</span>.
            </li>

            <li>
              Select the <span className="font-semibold">trajectories</span>,{' '}
              <span className="font-semibold">seasons</span>,{' '}
              <span className="font-semibold">conditions</span>, and{' '}
              <span className="font-semibold">sensors</span> of interest.
            </li>

            <li>
              Select deployments to download using either the{' '}
              <span className="font-semibold">Gallery View</span> or{' '}
              <span className="font-semibold">List View</span>.
            </li>

            <li>
              Click the <span className="font-semibold">Download</span> button.
              <ul className="list-disc list-inside ml-6 mt-3 space-y-1 text-base">
                <li>
                  ✔️ Human-readable → a bash script is downloaded. Install{' '}
                  <a
                    href="https://aws.amazon.com/cli/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-semibold transition-colors"
                  >
                    AWS CLI
                  </a>{' '}
                  and run it to retrieve your data.
                </li>
                <li>
                  MCAP download is not yet available. Please use the Human readable format in the meantime.
                </li>
              </ul>
              <p className="mt-2">
                💾 Ensure sufficient local storage. The full dataset is about{' '}
                <span className="font-semibold">8.8&nbsp;TB</span>.
              </p>
            </li>
          </ol>
        </div>
      </section>
      <section className="mb-20 px-4 sm:px-6 lg:px-8">
        <h2
          id="data-organization"
          className="scroll-mt-24 text-3xl sm:text-4xl text-center font-extrabold tracking-tight mb-6"
        >
          Data Organization
        </h2>

        <p className="text-base sm:text-lg text-justify max-w-5xl mx-auto mb-8">
          {' '}
          The FoMo dataset is split into deployments, which include multiple
          trajectories. These deployments are identified by the deployment day
          or by the first day of a deployment week as{' '}
          <code className="bg-gray-100 dark:bg-gray-800 text-sm font-mono px-1.5 py-0.5 rounded">
            {''}
            YYYY-MM-DD{''}
          </code>
          {''}. Subsequently, recordings are then located within the deployments
          folders, with the format{' '}
          <code className="bg-gray-100 dark:bg-gray-800 text-sm font-mono px-1.5 py-0.5 rounded">
            {''}
            name-YYYY-MM-DD-HH-mm{''}
          </code>
          {''}, referring to the name and start of the trajectory. Each
          recording contains all sensor data, the GT trajectory and covariance,
          meteorological data, and calibration files.{''}
        </p>

        <DatasetTree />
      </section>
      {/*        <section className="mb-20 px-4 sm:px-6 lg:px-8">
            <h2
                id="devkit-sdk"
                className="scroll-mt-24 text-3xl sm:text-4xl text-center font-extrabold tracking-tight mb-6"
            >
                    Devkit (SDK)
            </h2>

            <p className="text-base sm:text-lg text-justify max-w-5xl mx-auto mt-6">
                We include a{" "}
                <a
                    href="https://github.com/norlab-ulaval/fomo-public-sdk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 font-semibold hover:underline hover:text-blue-800"
                >
                    public Software Development Kit (SDK)
                </a>{" "}
                for data conversion, calibration, and algorithm evaluation.
            </p>
        </section>*/}
    </div>
  );
}
